import React, { useState } from 'react';
import { Order, loadOrderWithItemGroups } from '../../../../../model/order';
import { updateInvoice, Invoice, deleteInvoice, InvoiceStatus } from '../../../../../model/invoice';
import { handleSuccess, handleError } from '../../../../../model/notification';
import { t } from '../../../../../locales/translate';
import { TableBlock } from '../../../../../components/TableBlock/TableBlock';
import { NavLink } from 'react-router-dom';
import { invoicePath, deliveryPath } from '../../../../../routes';
import { Icon } from '../../../../../components/Icon/Icon';
import { SelectableList } from '../../../../../components/SelectableList/SelectableList';
import { Spinner } from '../../../../../components/Spinner/Spinner';
import {
  Delivery,
  updateDelivery,
  getDeliveryStatus,
  deleteDelivery,
  DeliveryStatus,
  removeDeliveryFromPicklist,
} from '../../../../../model/delivery';
import { formatDateTime } from '../../../../../util/util';
import { Tooltip } from '../../../../../components/Tooltip/Tooltip';
import { DocumentStatus } from '../../../../../model/document';
import { compact } from 'lodash';

interface ContextMenuItem {
  id: string;
  label: string;
  callback: () => void;
}

interface Props {
  order: Order;
  document: Invoice | Delivery;
  documentType: 'invoice' | 'delivery';
  displayDate?: boolean;
  displayContextMenu?: boolean;
  displayDelete?: boolean;
  incoming?: boolean;
  footer?: React.ReactNode;
  onClick?: () => void;
  onChange?: () => void;
}

function reassignOption(
  document: Invoice | Delivery,
  incoming: boolean,
  onClick: (() => void) | undefined
): ContextMenuItem | null {
  if (incoming && ['draft', 'pending', 'waiting'].includes(document?.status)) {
    return {
      id: 'reassing',
      label: t.actions.edit(''),
      callback: () => {
        if (onClick) {
          onClick();
        }
      },
    };
  }
  return null;
}

function placeOption(
  document: Invoice | Delivery,
  changeDocumentStatus: (status: InvoiceStatus | DeliveryStatus) => Promise<void>
): ContextMenuItem | null {
  if (document.status === 'draft') {
    return {
      id: 'place',
      label: `${t.actions.place('')} (${t.general.withoutNotification})`,
      callback: () => {
        changeDocumentStatus('pending');
      },
    };
  }
  return null;
}

function placeAndBookOption(
  document: Invoice | Delivery,
  documentType: 'invoice' | 'delivery',
  changeDocumentStatus: (status: DocumentStatus) => Promise<void>
): ContextMenuItem | null {
  if (['draft', 'pending', 'waiting', 'fulfillment'].includes(document?.status)) {
    return {
      id: 'place-and-book',
      label:
        document.status === 'draft'
          ? `${t.actions.placeAndBook} (${t.general.withoutNotification})`
          : `${t.actions.book('')} (${t.general.withoutNotification})`,
      callback: () => {
        if (documentType === 'invoice') {
          changeDocumentStatus('booked' as InvoiceStatus);
        }
        if (documentType === 'delivery') {
          changeDocumentStatus('sent' as DeliveryStatus);
        }
      },
    };
  }
  return null;
}

function removeFromPicklistOption(
  document: Invoice | Delivery,
  documentType: 'invoice' | 'delivery',
  onRemoveFromPicklist: (delivery?: Delivery) => Promise<void>
): ContextMenuItem | false {
  return (
    documentType === 'delivery' &&
    !!(document as Delivery)?.picklistId && {
      id: 'remove-from-picklist',
      label: t.model.delivery.removeFromPicklist,
      callback: () => onRemoveFromPicklist(document as Delivery),
    }
  );
}

export function DocumentCell({
  order,
  document,
  documentType,
  displayDate,
  displayContextMenu = true,
  displayDelete,
  footer,
  incoming,
  onClick,
  onChange,
}: Props): JSX.Element | null {
  const [loading, setLoading] = useState(false);

  async function changeDocumentStatus(status: InvoiceStatus | DeliveryStatus): Promise<void> {
    setLoading(true);
    try {
      if (documentType === 'invoice') {
        await updateInvoice({
          orderId: document.orderId,
          invoiceId: document.id,
          skipEmailDelivery: true,
          status: status as InvoiceStatus,
        });
        handleSuccess(status === 'pending' ? t.model.invoice.invoicePlaced : t.model.invoice.invoiceBooked);
      }
      if (documentType === 'delivery') {
        await updateDelivery(document.id, {
          orderId: document.orderId,
          skipEmailDelivery: true,
          status: status as DeliveryStatus,
        });
        handleSuccess(status === 'pending' ? t.model.delivery.deliveryPlaced : t.model.delivery.deliveryBooked);
      }
      onChange?.();
      await loadOrderWithItemGroups(order.id);
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  }

  async function onRemoveFromPicklist(delivery?: Delivery): Promise<void> {
    if (!delivery?.picklistId) {
      return;
    }

    setLoading(true);
    try {
      await removeDeliveryFromPicklist(delivery.id);
      onChange?.();
      handleSuccess(t.model.delivery.removedFromPicklist);
      await loadOrderWithItemGroups(order.id);
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  }

  async function handleDeleteDocument(): Promise<void> {
    setLoading(true);
    try {
      if (document.orderId) {
        if (documentType === 'invoice') {
          await deleteInvoice(document.orderId, document.id);
        }
        if (documentType === 'delivery') {
          await deleteDelivery(document.orderId, document.id);
        }
      }
      onChange?.();
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  }

  const options = compact([
    reassignOption(document, !!incoming, onClick),
    placeOption(document, changeDocumentStatus),
    placeAndBookOption(document, documentType, changeDocumentStatus),
    removeFromPicklistOption(document, documentType, onRemoveFromPicklist),
  ]);

  let link = '';
  let status: string | undefined = undefined;

  if (documentType === 'invoice') {
    link = invoicePath(order.id, document.id);
    status = t.model.status[document.status];
  }
  if (documentType === 'delivery') {
    link = deliveryPath(order.id, document.id);
    status = t.model.status[getDeliveryStatus(document.status as DeliveryStatus)];
  }

  const allowDeleteDocument = displayDelete && document.$permissions.includes('destroy');

  return (
    <TableBlock
      className="order-items__document"
      header={
        <NavLink className="order-items__button-link button button--link" to={link}>
          <div>
            <div>{displayDate && document.createdAt && formatDateTime(document.createdAt)}</div>
            <div>{document.reference}</div>
          </div>
        </NavLink>
      }
      footer={
        <>
          <div>{status}</div>
          <div>{footer}</div>
        </>
      }
      icon={
        <>
          {displayContextMenu && options.length > 0 && (
            <Tooltip
              position="left-start"
              reference={<Icon name="lightning--sm" />}
              mode="click"
              rounded={false}
              arrow={false}
            >
              <SelectableList
                options={
                  loading
                    ? [<Spinner key="loading" color="gray" scale="1" />]
                    : options.map((option) => {
                        return (
                          <div key={option.id} onClick={option.callback}>
                            {option.label}
                          </div>
                        );
                      })
                }
              />
            </Tooltip>
          )}
          {allowDeleteDocument && (
            <>
              {loading ? (
                <Spinner key="loading" color="gray" scale="1" />
              ) : (
                <Icon
                  name="trash--sm"
                  onClick={() => {
                    handleDeleteDocument();
                  }}
                />
              )}
            </>
          )}
        </>
      }
    />
  );
}
